import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import type {
  ApiToolConnectionHeader,
  ApiToolParameterBinding,
  CreateApiToolConnectionRequest,
  ImportOpenApiSpecResult,
  UpdateApiToolConnectionRequest,
} from '../types/ApiTools';

export type ConnectionFormState = {
  connectionId: string;
  displayName: string;
  description: string;
  baseUrl: string;
  authType: string;
  authHeaderName: string;
  authBaseUrl: string;
  authTokenUrl: string;
  authValue: string;
  authBasicUsername: string;
  authBasicPassword: string;
  clearAuthValue: boolean;
  oauthGrantType: string;
  oauthAuthorizationUrl: string;
  oauthTokenUrl: string;
  oauthScopes: string;
  oauthClientId: string;
  oauthClientSecret: string;
  clearOAuthClientSecret: boolean;
  clearOAuthTokens: boolean;
  defaultHeaders: ApiToolConnectionHeader[];
};

export const emptyConnectionForm: ConnectionFormState = {
  connectionId: '',
  displayName: '',
  description: '',
  baseUrl: '',
  authType: 'none',
  authHeaderName: 'X-Api-Key',
  authBaseUrl: '',
  authTokenUrl: '',
  authValue: '',
  authBasicUsername: '',
  authBasicPassword: '',
  clearAuthValue: false,
  oauthGrantType: 'authorizationCode',
  oauthAuthorizationUrl: '',
  oauthTokenUrl: '',
  oauthScopes: '',
  oauthClientId: '',
  oauthClientSecret: '',
  clearOAuthClientSecret: false,
  clearOAuthTokens: false,
  defaultHeaders: [],
};

export const emptyManualToolForm = {
  toolId: '',
  displayName: '',
  kernelFunctionName: '',
  description: '',
  httpMethod: 'GET',
  pathTemplate: '/',
  parameterBindings: [] as ApiToolParameterBinding[],
  responseMode: 'full',
  responseJsonPath: '',
  isPublished: false,
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
};

export function suggestKernelFunctionName(displayName: string): string {
  const words = displayName
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  const candidate = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

  return candidate.length > 64 ? candidate.slice(0, 64) : candidate;
}

export function normalizeParameterBindingsForSave(
  bindings: ApiToolParameterBinding[],
): ApiToolParameterBinding[] {
  return bindings
    .map((binding) => ({
      name: binding.name.trim(),
      description: binding.description.trim(),
      location: binding.location.trim() || 'query',
      sourceName: binding.sourceName.trim() || binding.name.trim(),
      isRequired: binding.isRequired,
      defaultValue: binding.defaultValue.trim(),
      example: binding.example.trim(),
    }))
    .filter((binding) => binding.name.length > 0);
}

export function isFetchedCredentialPair(value: string): boolean {
  const separatorIndex = value.indexOf(':');
  return separatorIndex > 0 && separatorIndex < value.length - 1;
}

export function validateConnectionAuth(
  form: ConnectionFormState,
  options: { credentialConfigured: boolean; oauthSecretConfigured: boolean },
): string | null {
  if (form.authType === 'apiKey') {
    if (!form.authHeaderName.trim()) {
      return 'authHeaderNameRequired';
    }

    if (!form.authValue.trim() && !form.clearAuthValue && !options.credentialConfigured) {
      return 'authValueRequired';
    }

    return null;
  }

  if (form.authType === 'bearer') {
    const tokenUrl = form.authTokenUrl.trim();
    const value = form.authValue.trim();
    if (tokenUrl) {
      if (!value && (form.clearAuthValue || options.credentialConfigured)) {
        return null;
      }

      return isFetchedCredentialPair(value) ? null : 'authClientCredentialsRequired';
    }

    if (!value && !form.clearAuthValue && !options.credentialConfigured) {
      return 'authValueRequired';
    }

    return null;
  }

  if (form.authType === 'basic') {
    const hasUsername = form.authBasicUsername.trim().length > 0;
    const hasPassword = form.authBasicPassword.length > 0;
    if (hasUsername !== hasPassword) {
      return hasUsername ? 'authBasicBothRequired' : 'authBasicUsernameRequired';
    }

    if (!hasUsername && !form.clearAuthValue && !options.credentialConfigured) {
      return 'authBasicBothRequired';
    }

    return null;
  }

  if (form.authType === 'oauth') {
    if (!form.oauthTokenUrl.trim()) {
      return 'oauthTokenUrlRequired';
    }

    if (form.oauthGrantType === 'authorizationCode' && !form.oauthAuthorizationUrl.trim()) {
      return 'oauthAuthorizationUrlRequired';
    }

    if (!form.oauthClientId.trim()) {
      return 'oauthClientIdRequired';
    }

    if (!form.oauthClientSecret.trim() && (form.clearOAuthClientSecret || !options.oauthSecretConfigured)) {
      return 'oauthClientSecretRequired';
    }

    return null;
  }

  return null;
}

export function buildAuthValueForSave(
  form: ConnectionFormState,
  options: { keepExisting: boolean },
): string | undefined {
  if (form.authType === 'basic') {
    const username = form.authBasicUsername.trim();
    const password = form.authBasicPassword;
    const hasUsername = username.length > 0;
    const hasPassword = password.length > 0;

    if (!hasUsername && !hasPassword) {
      return undefined;
    }

    if (options.keepExisting && hasUsername !== hasPassword) {
      return undefined;
    }

    if (!hasUsername) {
      return undefined;
    }

    return `${username}:${password}`;
  }

  const trimmed = form.authValue.trim();
  return trimmed || undefined;
}

export function requiresStaticAuthCredential(authType: string): boolean {
  return authType === 'apiKey' || authType === 'bearer' || authType === 'basic';
}

export function applySuggestedAuthToForm(
  form: ConnectionFormState,
  suggestedAuth: ImportOpenApiSpecResult['suggestedAuth'],
): ConnectionFormState {
  if (!suggestedAuth || form.authType !== 'none' || form.authValue.trim()) {
    return form;
  }

  if (suggestedAuth.authType === 'oauth') {
    return {
      ...form,
      authType: 'oauth',
      oauthGrantType: suggestedAuth.oauthGrantType || form.oauthGrantType,
      oauthAuthorizationUrl: suggestedAuth.oauthAuthorizationUrl || form.oauthAuthorizationUrl,
      oauthTokenUrl: suggestedAuth.oauthTokenUrl || form.oauthTokenUrl,
      oauthScopes: suggestedAuth.oauthScopes || form.oauthScopes,
    };
  }

  return {
    ...form,
    authType: suggestedAuth.authType || form.authType,
    authHeaderName: suggestedAuth.authHeaderName || form.authHeaderName,
  };
}

export function normalizeDefaultHeadersForSave(
  headers: ApiToolConnectionHeader[],
): ApiToolConnectionHeader[] {
  return headers
    .map((header) => ({
      name: header.name.trim(),
      value: header.value.trim(),
    }))
    .filter((header) => header.name.length > 0);
}

export function createEmptyDefaultHeader(): ApiToolConnectionHeader {
  return { name: '', value: '' };
}

function usesAuthTokenUrl(authType: string): boolean {
  return authType === 'bearer' || authType === 'basic';
}

function usesAuthBaseUrl(authType: string): boolean {
  return usesAuthTokenUrl(authType) || authType === 'oauth';
}

export function buildCreateConnectionRequest(form: ConnectionFormState): CreateApiToolConnectionRequest {
  const authValue = buildAuthValueForSave(form, { keepExisting: false });
  return {
    connectionId: form.connectionId.trim() || undefined,
    displayName: form.displayName.trim(),
    description: form.description.trim(),
    baseUrl: form.baseUrl.trim(),
    authType: form.authType,
    authHeaderName: form.authType === 'apiKey' ? form.authHeaderName.trim() : '',
    authBaseUrl: usesAuthBaseUrl(form.authType) ? form.authBaseUrl.trim() : '',
    authTokenUrl: usesAuthTokenUrl(form.authType) ? form.authTokenUrl.trim() : '',
    authValue,
    oauthGrantType: form.authType === 'oauth' ? form.oauthGrantType : undefined,
    oauthAuthorizationUrl: form.authType === 'oauth' ? form.oauthAuthorizationUrl.trim() : undefined,
    oauthTokenUrl: form.authType === 'oauth' ? form.oauthTokenUrl.trim() : undefined,
    oauthScopes: form.authType === 'oauth' ? form.oauthScopes.trim() : undefined,
    oauthClientId: form.authType === 'oauth' ? form.oauthClientId.trim() : undefined,
    oauthClientSecret: form.authType === 'oauth' ? form.oauthClientSecret.trim() || undefined : undefined,
    defaultHeaders: normalizeDefaultHeadersForSave(form.defaultHeaders),
  };
}

export function buildUpdateConnectionRequest(
  form: ConnectionFormState,
  options: { keepExisting: boolean },
): UpdateApiToolConnectionRequest {
  const authValue = buildAuthValueForSave(form, options);
  return {
    displayName: form.displayName.trim(),
    description: form.description.trim(),
    baseUrl: form.baseUrl.trim(),
    authType: form.authType,
    authHeaderName: form.authType === 'apiKey' ? form.authHeaderName.trim() : '',
    authBaseUrl: usesAuthBaseUrl(form.authType) ? form.authBaseUrl.trim() : '',
    authTokenUrl: usesAuthTokenUrl(form.authType) ? form.authTokenUrl.trim() : '',
    authValue,
    clearAuthValue: form.clearAuthValue,
    oauthGrantType: form.authType === 'oauth' ? form.oauthGrantType : undefined,
    oauthAuthorizationUrl: form.authType === 'oauth' ? form.oauthAuthorizationUrl.trim() : '',
    oauthTokenUrl: form.authType === 'oauth' ? form.oauthTokenUrl.trim() : '',
    oauthScopes: form.authType === 'oauth' ? form.oauthScopes.trim() : '',
    oauthClientId: form.authType === 'oauth' ? form.oauthClientId.trim() : '',
    oauthClientSecret: form.authType === 'oauth' ? form.oauthClientSecret.trim() || undefined : undefined,
    clearOAuthClientSecret: form.authType !== 'oauth' || form.clearOAuthClientSecret,
    clearOAuthTokens: form.authType !== 'oauth' || form.clearOAuthTokens,
    defaultHeaders: normalizeDefaultHeadersForSave(form.defaultHeaders),
  };
}

export function createEmptyParameterBinding(): ApiToolParameterBinding {
  return {
    name: '',
    description: '',
    location: 'query',
    sourceName: '',
    isRequired: false,
    defaultValue: '',
    example: '',
  };
}
